import {ExternalLinkHandlerBase} from "./external-link-handler-base";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {
    ExternalLinkIframeRenderer,
    ExternalLinkIframeRendererOptions
} from "../renderers/external-link-iframe.renderer";
import {GNWP_SOURCES} from "../../gnwp/gnwp-consts";
import {
    NullableUndefinedNumber,
    NullableUndefinedString, UndefinedNumber,
    UndefinedString
} from "../../../../common/types/nullable-types";
import {
    AreaType,
    ControlCenterDeeplink,
    ControlCenterOpenSource,
    IChannel,
    IControlCenterFullSpecificParams
} from "@sparkware/uc-sdk-core";
import {isCashoutSessionType, isDepositSessionType} from "../../../../common/utils/cashier-util";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../../common/const/casinofied-analytics-consts";
import {OpenCashierOptions, OpenDepositOptions} from "../../../../common/interfaces/cashier.service.interface";
import {Check} from "@sparkware/app-utils";
import {CIPropRead} from "../../../../utils/case-insensitive-property-reader";
import {MyAccountTab} from "@sparkware/uc-sdk-core";
import {ISendElasticEventOptions} from "../../analytics/analytics.service.interface";

export type ExternalLinkInIframeRendererParams = Omit<ExternalLinkIframeRendererOptions, 'onClosed'>;

export abstract class ExternalLinkInIframeHandlerBase extends ExternalLinkHandlerBase {
    constructor(services: IServiceFactoryExtended) {
        super(services);
        this.addSdkEventSubscription(this.services.sdk.events.onPostMessage(this._handlePostMessage));
    }

    protected abstract _onPostMessageReceived(message: MessageEvent): Promise<void>;
    protected abstract _getGnwpSource(): GNWP_SOURCES;

    protected _parseMessageData(message: MessageEvent): any {
        if(Check.isString(message.data)) {
            try {
                return JSON.parse(message.data);
            } catch (err) {
                this.services.logger.error(`Failed to parse external link post message data: ${message.data}`, err);
            }

        }

        return message.data;
    }

    protected _extractActionId(message: MessageEvent): NullableUndefinedString {
        return CIPropRead.getStringProperty('actionId', this._parseMessageData(message));
    }

    protected _extractUrl(message: MessageEvent): NullableUndefinedString {
        return CIPropRead.getStringProperty('url', this._parseMessageData(message));
    }

    protected _extractGameId(message: MessageEvent): NullableUndefinedNumber {
        return CIPropRead.getNumericProperty('gameId', this._parseMessageData(message));
    }

    protected _extractPromoCode(message: MessageEvent): NullableUndefinedString {
        return  CIPropRead.getStringProperty("PromCode", this._parseMessageData(message))
                || CIPropRead.getStringProperty("promoCode", this._parseMessageData(message));
    }

    protected _extractCashierTabId(message: MessageEvent): UndefinedNumber {
        return  CIPropRead.getNumericProperty("TabId", this._parseMessageData(message)) ?? undefined;
    }

    protected _extractMyAccountTabId(message: MessageEvent): MyAccountTab | undefined {
        return  CIPropRead.getNumericProperty("TabId", this._parseMessageData(message)) ?? undefined;
    }


    private _handlePostMessage = async (message: MessageEvent) => {
        if(message.source !== this._renderer?.iframeElementRef?.contentWindow) {
            return;
        }

        this._sendAnalytics(message);
        await this._onPostMessageReceived(message);
    }

    protected _sendAnalytics(message: MessageEvent): void {
        try {
            const analyticsInfo: ISendElasticEventOptions = {
                eventType: 'CGP_GENERIC_EVENT',
                category: this._getGnwpSource(),
                action: 'External Link PostMessage',
                label: message.data ? JSON.stringify(message.data) : null,
                value: this.additionalAnalyticsInfo
            }

            this.services.analytics.sendElasticInfoEvent(analyticsInfo);

        } catch (err) {
            this.services.logger.error(`Failed to send External Link PostMessage analytics event: ${err}`);
        }
    }

    protected _renderer: ExternalLinkIframeRenderer | null = null;
    protected _getRenderer(paramsOverride?: Partial<ExternalLinkInIframeRendererParams>): ExternalLinkIframeRenderer {
        if(!this._renderer) {
            this._renderer = new ExternalLinkIframeRenderer(this.services, {
                ...this._getIframeRendererParams(),
                ...paramsOverride,
                onClosed: this._onExternalLinkClosed
            });
        }

        return this._renderer;
    }

    protected abstract _getIframeRendererParams(): ExternalLinkInIframeRendererParams;

    protected _onExternalLinkClosed = async () => {
        this.services.sdk.eventsTrigger.externalLinkClosed({
            gnwpSource: this._getGnwpSource()
        });
        this.dispose();
    }

    protected _closeExternalLinkDialog = () => {
        this._renderer?.dispose();
        this._renderer = null;
    }

    protected async _showExternalLinkDialog(url: string, paramsOverride?: Partial<ExternalLinkInIframeRendererParams>): Promise<void>  {
        this.services.sdk.eventsTrigger.closeAllSecondWebViews();
        await this._getRenderer(paramsOverride).showInModalDialog(url);
    }

    protected _renderExternalLinkIframe(url: string, paramsOverride?: Partial<ExternalLinkInIframeRendererParams>) {
        return this._getRenderer(paramsOverride).renderAsReactElement(url);
    }

    protected async _redirect(url: string): Promise<void> {
        if(this._getIframeRendererParams().httpVerb === 'POST') {
            this.services.dom.performFormPostForUrl(url, {
                target: '_self'
            });
        } else {
            await this.services.location.redirect(url);
        }
    }

    protected _openInNewTab(url: string): void {
        if(this._getIframeRendererParams().httpVerb === 'POST') {
            this.services.dom.performFormPostForUrl(url, {
                target: '_blank'
            });
        } else {
            super._openInNewTab(url);
        }
    }

    protected async _openQuickDeposit(options: {promCode: UndefinedString; channel: IChannel}): Promise<void> {
        this._closeExternalLinkDialog();
        const openDepositOptions: OpenDepositOptions = {
            channel: options.channel
        };

        if(options.promCode) {
            openDepositOptions.PromCode = options.promCode
        }

        await this.services.cashier.openDeposit(openDepositOptions);
    }

    protected async _openCashier(options: {
        tabId: UndefinedNumber;
        promCode: NullableUndefinedString;
        channel: {
            area: AreaType;
            element?: string;
            source: string;
        }
    }): Promise<void> {

        this._closeExternalLinkDialog();

        let channelElement = options.channel.element;

        if(!channelElement) {
            if (isDepositSessionType(options.tabId)) {
                channelElement = CHANNEL_ELEMENTS.depositButton;
            } else if (isCashoutSessionType(options.tabId)) {
                channelElement = CHANNEL_ELEMENTS.withdrawButton;
            }
        }

        const params: OpenCashierOptions = {
            channel: {
                source: options.channel.source,
                element: channelElement,
                area: options.channel.area
            }
        }

        if(options.promCode) {
            params.PromCode = options.promCode;
        }

        if(options.tabId) {
            params.sessionType = options.tabId;
        }

        await this.services.cashier.openCashier(params);
    }

    protected async _redirectToURL(message:MessageEvent):Promise<void>{
        if('redirectclient' === this._getParsedData('message', message)?.toLowerCase()) {
            const url = this._getParsedData('url', message);
            if(url) {
                if(this.services.device.hybrid.isHybrid)
                {
                    this.services.hybrid.openExternalLink(url);
                }
                else
                {
                    await this.services.location.redirect(url)
                }
            }
        }
    }

    private _getParsedData(propName:string, message: MessageEvent<any>):string{
        return CIPropRead.getStringProperty(propName, this._parseMessageData(message));
    }

    protected _extractChannel(message: MessageEvent): IChannel {
        const parsedChannelData = CIPropRead.getShallowLowercasedObject('channel', this._parseMessageData(message)) || {};
        const {area = AreaType.FullCashier, element = CHANNEL_ELEMENTS.cashierAction, source = CHANNEL_SOURCE.cashier} = parsedChannelData;
        return {
            area:area,
            element: element,
            source: source
        };
    }

    protected _extractAppSpecificParameters(message: MessageEvent): IControlCenterFullSpecificParams {
        const parsedAppSpecificParamsData = CIPropRead.getShallowLowercasedObject('appSpecificParameters', this._parseMessageData(message)) || {};
        const {deeplink = ControlCenterDeeplink.Main, source = ControlCenterOpenSource.Cashier} = parsedAppSpecificParamsData;
        return {
            deeplink: deeplink,
            source: source
        };
    }

    protected get additionalAnalyticsInfo():string{
        return null;//override in specialized classes if you need extra info reported to analytics.
    }

}